import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Buildings, UsersThree, ChartLine, Briefcase } from "@phosphor-icons/react";

const fmt = (n) => (Number(n) || 0).toFixed(2);

export default function AdminWidgets() {
    const [data, setData] = useState(null);

    useEffect(() => {
        http.get("/admin/dashboard")
            .then((r) => setData(r.data))
            .catch(() => null);
    }, []);

    if (!data) {
        return (
            <div className="border border-slate-200 bg-white p-6 mt-6">
                <div className="tracing-beam h-1 bg-slate-100" />
            </div>
        );
    }

    const total = data.total_month || 0;
    const bPct = data.billable_pct || 0;
    const nbPct = total > 0 ? 100 - bPct : 0;

    return (
        <div className="space-y-6 mt-6" data-testid="admin-widgets">
            {/* Billable vs non-billable */}
            <div className="border border-slate-200 bg-white">
                <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Mes en curso · facturación
                        </div>
                        <div className="font-display text-xl font-semibold mt-1">
                            Horas facturables vs no facturables
                        </div>
                    </div>
                    <div className="flex gap-6 text-sm">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Activos
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="flex items-center gap-1 font-mono text-slate-800">
                                    <UsersThree size={14} /> {data.active_users}
                                </span>
                                <span className="flex items-center gap-1 font-mono text-slate-800">
                                    <Briefcase size={14} /> {data.active_projects}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200">
                    <div className="bg-white p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700">
                            Facturables
                        </div>
                        <div
                            className="font-mono text-3xl font-bold mt-2 text-slate-950"
                            data-testid="admin-billable-hours"
                        >
                            {fmt(data.billable_hours)} h
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            {bPct.toFixed(1)}% del total
                        </div>
                    </div>
                    <div className="bg-white p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600">
                            No facturables
                        </div>
                        <div
                            className="font-mono text-3xl font-bold mt-2 text-slate-950"
                            data-testid="admin-nonbillable-hours"
                        >
                            {fmt(data.non_billable_hours)} h
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            {nbPct.toFixed(1)}% del total
                        </div>
                    </div>
                    <div className="bg-white p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Total mes
                        </div>
                        <div className="font-mono text-3xl font-bold mt-2 text-slate-950">
                            {fmt(total)} h
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Mes desde {data.month_start}
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-200">
                    <div className="h-3 bg-slate-100 flex overflow-hidden" data-testid="admin-billable-bar">
                        <div
                            className="bg-[#002BAF] h-full"
                            style={{ width: `${bPct}%` }}
                            title={`Facturables ${bPct.toFixed(1)}%`}
                        />
                        <div
                            className="bg-amber-500 h-full"
                            style={{ width: `${nbPct}%` }}
                            title={`No facturables ${nbPct.toFixed(1)}%`}
                        />
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-2 h-2 bg-[#002BAF]" />
                            Facturables
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-2 h-2 bg-amber-500" />
                            No facturables (vacaciones, formación, internos…)
                        </span>
                    </div>
                </div>
            </div>

            {/* Project progress */}
            <div className="border border-slate-200 bg-white">
                <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Año en curso · proyectos
                        </div>
                        <div className="font-display text-xl font-semibold mt-1">
                            Evolución por proyecto (real / planificado)
                        </div>
                    </div>
                    <ChartLine size={28} className="text-slate-300" />
                </div>
                <div className="divide-y divide-slate-100" data-testid="admin-project-progress">
                    {data.project_progress.length === 0 && (
                        <div className="text-center py-12 text-slate-500 text-sm">
                            Sin datos suficientes este año.
                        </div>
                    )}
                    {data.project_progress.map((p) => {
                        const pct = p.pct;
                        const cap = pct === null ? 0 : Math.min(pct, 100);
                        const overrun = pct !== null && pct > 100;
                        const barColor = overrun
                            ? "bg-red-600"
                            : pct !== null && pct > 80
                              ? "bg-amber-500"
                              : "bg-[#002BAF]";
                        return (
                            <div
                                key={p.project_id}
                                className="px-6 py-3 grid grid-cols-12 items-center gap-3 hover:bg-slate-50"
                            >
                                <div className="col-span-5 min-w-0">
                                    <div className="text-sm font-medium text-slate-900 truncate">
                                        {p.project_name}
                                    </div>
                                    <div className="text-[11px] font-mono text-slate-500 flex items-center gap-1">
                                        <Buildings size={10} /> {p.customer_name}
                                        {p.project_code && (
                                            <span className="ml-2 text-slate-400">
                                                · {p.project_code}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-5">
                                    <div className="h-2 bg-slate-100 relative">
                                        <div
                                            className={`absolute inset-y-0 left-0 ${barColor}`}
                                            style={{ width: `${cap}%` }}
                                        />
                                        {overrun && (
                                            <div
                                                className="absolute inset-y-0 right-0 bg-red-700"
                                                style={{ width: "4px" }}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-2 text-right font-mono tabular-nums text-sm">
                                    {pct === null ? (
                                        <span className="text-slate-400 text-xs">
                                            sin plan
                                        </span>
                                    ) : (
                                        <span
                                            className={`font-semibold ${overrun ? "text-red-700" : "text-slate-900"}`}
                                        >
                                            {pct.toFixed(0)}%
                                        </span>
                                    )}
                                    <div className="text-[11px] text-slate-500">
                                        {p.actual_hours}h
                                        {p.planned_hours > 0 && ` / ${p.planned_hours}h`}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
